/* eslint-disable */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from 'react';
// @ts-ignore
import logoImage from './tbl_logo.png';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { supabase } from './lib/supabase';

// Page Components
import Login from './pages/Login';
import NGODashboard from './pages/NGODashboard';
import Home from './pages/Home';
import ContactUs from './pages/ContactUs';
import CSRCalculator from './pages/CSRCalculator';
import Why from './pages/Why';
import What from './pages/What';
import How from './pages/How';

// Company Pages ---
import CompanyFirstRun from './pages/CompanyFirstRun';
import CompanyAcceptInvite from './pages/company/CompanyAcceptInvite';
import CompanyDashboard from './pages/company/CompanyDashboard';
import JourneySoFar from './pages/company/JourneySoFar';
import CompanySettings from './pages/company/CompanySettings';
import CompanyProjects from './pages/company/CompanyProjects';
import ProjectReview from './pages/company/ProjectReview';
import CompanyCompliance from './pages/company/CompanyCompliance';
import CompanyFinance from './pages/company/CompanyFinance';
import CompanyImpact from './pages/company/CompanyImpact';
import CompanyMOMTracker from './pages/company/CompanyMOMTracker';

import NGOInvite from './pages/company/NGOInvite';
import NGOReview from './pages/company/NGOReview';
import ProjectCreate from './pages/company/ProjectCreate';
import ProjectAssign from './pages/company/ProjectAssign';
import PreStartChecklist from './pages/company/PreStartChecklist';
import FundRequestReview from './pages/company/FundRequestReview';
import ProjectClosure from './pages/company/ProjectClosure';
import Subscription from './pages/company/Subscription';
import NotificationBell from './components/NotificationBell';
import Footer from './components/Footer';


// NGO Pages ---
import NGOFinance from './pages/ngo/NGOBudget';
import NGOImpact from './pages/ngo/NGOImpact';
import NGOComplianceTracker from './pages/ngo/NGOComplianceTracker';
import NGOFirstRun from './pages/ngo/NGOFirstRun';
import NGOSettings from './pages/ngo/NGOSettings';
import ProjectAccept from './pages/ngo/ProjectAccept';
import ProjectDetails from './pages/ngo/ProjectDetails';
import NGOProjects from './pages/ngo/NGOProjects';
import FundRequest from './pages/ngo/FundRequest';
import KPIReport from './pages/ngo/KPIReport';
import NGOSignup from './pages/NGOSignup';
import AcceptInvite from './pages/ngo/AcceptInvite';
import AdminDashboard from './pages/admin/AdminDashboard';
import CorporateFinancials from './pages/company/CorporateFinancials';
import WaitingAssignment from './pages/WaitingAssignment';

//==============================================================================
// AUTHENTICATION & ROUTING LOGIC
//==============================================================================
import { AuthProvider } from './contexts/AuthContext';
import { CustomTooltipLink } from './components/CustomTooltip';

import { useTabBadges } from './hooks/useTabBadges';

// ... imports remain the same

// Helper for badge rendering
const BadgeDot = () => (
    <span
        style={{
            position: 'absolute',
            top: '-2px',
            right: '-8px',
            width: '8px',
            height: '8px',
            backgroundColor: '#ef4444',
            borderRadius: '50%',
            boxShadow: '0 0 0 1.5px white',
            zIndex: 10
        }}
    />
);

const AppContent: React.FC = () => {
    // ... existing state ...
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [userRole, setUserRole] = useState<string | null>(null);
    const [userProfile, setUserProfile] = useState<any>(null);
    const [loadingTimeout, setLoadingTimeout] = useState(false);
    const [hydrationComplete, setHydrationComplete] = useState(false);
    const hydrationCompleteRef = useRef(false);
    const location = useLocation();
    const navigate = useNavigate();
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const profileMenuRef = useRef<HTMLDivElement>(null);

    // Fetch badges
    const badges = useTabBadges(
        userRole,
        userProfile?.company_id,
        userProfile?.ngo_id
    );


    // ... existing useEffects ...

    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    // ... existing auth logic ...
    const resetUserState = useCallback((redirect: boolean = false) => {
        if (isMounted.current) {
            setUser(null);
            setUserRole(null);
            setUserProfile(null);
            setIsProfileMenuOpen(false);
            lastHydratedUserId.current = null;
            hydrationCompleteRef.current = false;
        } else {
            console.warn('[Auth] Skipped state reset because component unmounted.');
        }
        if (redirect) {
            navigate('/', { replace: true });
        }
    }, [navigate]);

    const clearSessionAndSignOut = useCallback(async () => {
        try {
            await supabase.auth.signOut();
        } catch (error) {
            console.error('Error during sign out:', error);
        } finally {
            resetUserState(false); // Don't redirect yet
            if (isMounted.current) {
                setLoading(false);
            }
            // Force full page reload to clear all states and caches
            window.location.href = '/';
        }
    }, [resetUserState]);

    useEffect(() => {
        if (!isProfileMenuOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
                setIsProfileMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isProfileMenuOpen]);

    // Normalize role helper (handles "Company User", "NGO User", "Admin", etc.)
    const normalizeRole = useCallback((role: string) => {
        if (!role) return null;
        const normalized = role.toLowerCase().replace(/\s+/g, '_');
        if (normalized.includes('admin')) return 'admin';
        if (normalized.includes('viewer')) return 'client'; // Viewers are essentially company clients
        if (normalized.includes('company')) return 'client'; // 'client' is the route type, 'company' is the entity
        if (normalized.includes('ngo')) return 'ngo';
        return null; // Only admin, company, and ngo roles are supported
    }, []);

    const lastHydratedUserId = useRef<string | null>(null);
    const hydrationInProgress = useRef<boolean>(false);
    const hasInitialized = useRef<boolean>(false);

    const hydrateUserFromSession = useCallback(async (session: { user: any } | null) => {
        if (!session?.user) {
            resetUserState();
            if (isMounted.current) {
                setLoading(false);
                setHydrationComplete(true);
                hydrationCompleteRef.current = true;
            }
            return;
        }

        // Avoid re-hydrating the same session repeatedly if already complete.
        if (session.user.id === lastHydratedUserId.current && hydrationCompleteRef.current && !hydrationInProgress.current) {
            return;
        }

        const prevUserId = lastHydratedUserId.current;
        lastHydratedUserId.current = session.user.id;
        hydrationInProgress.current = true;

        if (isMounted.current) {
            // ONLY set loading to true if we don't already have a user or if the user has changed.
            // This prevents "flicker" and unmounting of the current page during background token refreshes.
            if (!prevUserId || prevUserId !== session.user.id) {
                setLoading(true);
            }
        }
        try {
            const sessionUser = session.user;
            if (isMounted.current) {
                setUser(sessionUser);
            }

            // 1. OPTIMISTIC HYDRATION:
            const metadata = sessionUser.user_metadata || {};
            const defaultRole = metadata.role || null;
            const normalizedDefault = normalizeRole(defaultRole) || null;

            // Set initial state derived from session - fast!
            if (isMounted.current) {
                // ONLY set the role if we found one in the session metadata
                if (normalizedDefault) {
                    setUserRole(normalizedDefault);
                }
                
                setUserProfile((prev: any) => ({
                    ...(prev || {}),
                    id: sessionUser.id,
                    email: sessionUser.email,
                    role: defaultRole || prev?.role,
                    company_id: metadata.company_id || prev?.company_id || null,
                    ngo_id: metadata.ngo_id || prev?.ngo_id || null,
                    profile_complete: prev?.profile_complete ?? true // Preserve or assume true to prevent flash
                }));

                // Resolve loading condition: Wait for background fetch if we are missing the role
                if (normalizedDefault && (metadata.company_id || metadata.ngo_id || normalizedDefault === 'admin')) {
                    setLoading(false);
                    setHydrationComplete(true);
                    hydrationCompleteRef.current = true;
                }
            }

            // 2. BACKGROUND DATA FETCH:
            (async () => {
                try {

                    // A. Fetch Profile (only columns that exist in profiles table)
                    let { data: profileData, error: profileError } = await supabase
                        .from('profiles')
                        .select('id, email, role')
                        .eq('id', sessionUser.id)
                        .single();

                    const isNgoSignup = window.location.pathname.includes('/ngo/signup');

                    if (profileError || !profileData) {
                        if (profileError?.code === 'PGRST116' || !profileData) {
                            console.warn('[Auth] Background: Profile not found. Waiting for setup or manual assignment.');
                        } else {
                            console.error('[Auth] Background: Profile fetch error:', profileError);
                        }
                    }

                    if (!profileData) {
                        if (isNgoSignup) {
                            // Stale session on signup page with no profile: sign out silently
                            // so the NGOSignup component renders for an unauthenticated user.
                            console.warn('[Auth] Stale session on /ngo/signup with no profile – signing out.');
                            await supabase.auth.signOut();
                            if (isMounted.current) {
                                setLoading(false);
                                setHydrationComplete(true);
                                hydrationCompleteRef.current = true;
                            }
                            return;
                        } else {
                            console.warn('[Auth] Background: Profile not found, attempting role discovery...');
                            let discoveredRole: string | null = null;
                            
                            // 1. Check NGO links
                            const { data: ngoLink } = await supabase.from('ngo_users').select('ngo_id').eq('user_id', sessionUser.id).maybeSingle();
                            if (ngoLink) {
                                discoveredRole = 'NGO User';
                            } else {
                                // 2. Check Company links
                                const { data: companyLink } = await supabase.from('company_users').select('company_id').eq('user_id', sessionUser.id).maybeSingle();
                                if (companyLink) {
                                    discoveredRole = 'Company User';
                                }
                            }

                            if (discoveredRole) {
                                const newProfile = { id: sessionUser.id, role: discoveredRole, email: sessionUser.email };
                                await supabase.from('profiles').insert([newProfile]);
                                profileData = newProfile;
                            } else {
                                console.error('[Auth] Background: Could not fetch or create profile.');
                                if (isMounted.current) {
                                    setLoading(false);
                                    setHydrationComplete(true);
                                    hydrationCompleteRef.current = true;
                                }
                                return;
                            }
                        }
                    }

                    let realRole = normalizeRole(profileData.role) || 'client';
                    const originalRole = realRole;

                    const impersonatedCompanyId = localStorage.getItem('admin_impersonated_company_id');
                    if (realRole === 'admin' && impersonatedCompanyId) {
                        realRole = 'client';
                    }

                    // Fetch ID from link tables based on role
                    let relevantIds: { company_id?: string | null, ngo_id?: string | null } = {
                        company_id: null,
                        ngo_id: null
                    };

                    let isProfileComplete = false;
                    let ngoStatus = 'invited';
                    let companySetupType: string | null = null;

                    if (realRole === 'client') {
                        let currentCompanyId = null;
                        if (originalRole === 'admin' && impersonatedCompanyId) {
                            currentCompanyId = impersonatedCompanyId;
                            isProfileComplete = true; // Admins bypassing setup
                        } else {
                            const { data: linkData } = await supabase
                                .from('company_users')
                                .select('company_id')
                                .eq('user_id', sessionUser.id)
                                .maybeSingle();

                            if (linkData?.company_id) {
                                currentCompanyId = linkData.company_id;
                            }
                        }

                        if (currentCompanyId) {
                            relevantIds.company_id = currentCompanyId;

                            const { data: companyData } = await supabase
                                .from('companies')
                                .select('profile_complete, name, cin, pan, address, csr_contact, setup_type')
                                .eq('id', currentCompanyId)
                                .single();

                            if (originalRole !== 'admin') {
                                isProfileComplete = companyData?.profile_complete === true;
                            }
                            companySetupType = companyData?.setup_type || null;

                            if (companyData && !isProfileComplete && originalRole !== 'admin') {
                                const hasName = !!companyData.name?.trim();
                                const hasCin = !!companyData.cin?.trim();
                                const hasPan = !!companyData.pan?.trim();
                                const hasAddress = !!(companyData.address?.street && companyData.address?.city && companyData.address?.pincode);
                                const hasContact = !!(companyData.csr_contact?.name && companyData.csr_contact?.phone);

                                if (hasName && hasCin && hasPan && hasAddress && hasContact) {
                                    isProfileComplete = true;
                                    supabase.from('companies').update({ profile_complete: true }).eq('id', currentCompanyId);
                                }
                            }
                        }
                    } else if (realRole === 'ngo') {
                        const { data: linkData } = await supabase
                            .from('ngo_users')
                            .select('ngo_id')
                            .eq('user_id', sessionUser.id)
                            .maybeSingle();

                        if (linkData?.ngo_id) {
                            relevantIds.ngo_id = linkData.ngo_id;

                            const { data: ngoData } = await supabase
                                .from('ngos')
                                .select('profile_complete, status')
                                .eq('id', linkData.ngo_id)
                                .single();

                            ngoStatus = ngoData?.status || 'invited';
                            isProfileComplete = (ngoData?.status === 'verified' && ngoData?.profile_complete === true);
                        }
                    }

                    if (isMounted.current) {
                        setUserRole(realRole);
                        setUserProfile((prev: any) => ({
                            ...prev,
                            role: profileData?.role || realRole,
                            original_role: originalRole,
                            profile_complete: isProfileComplete,
                            company_id: relevantIds.company_id,
                            ngo_id: relevantIds.ngo_id,
                            setup_type: companySetupType
                        }));

                        // Critical for refresh stability: ensure loading is resolved even if optimistic path was skipped
                        setLoading(false);
                        setHydrationComplete(true);
                        hydrationCompleteRef.current = true;
                    }

                } catch (bgError) {
                    console.error('[Auth] Background synchronization failed:', bgError);
                    if (isMounted.current) {
                        setLoading(false);
                        setHydrationComplete(true);
                        hydrationCompleteRef.current = true;
                    }
                } finally {
                    hydrationInProgress.current = false;
                }
            })();

        } catch (error: any) {
            console.error('Failed to hydrate user session (initial step):', error);
            await clearSessionAndSignOut();
            setLoading(false);
            setHydrationComplete(true);
            hydrationCompleteRef.current = true;
            hydrationInProgress.current = false;
        }
    }, [resetUserState, normalizeRole, clearSessionAndSignOut]);

    useEffect(() => {
        const initialize = async () => {
            if (hasInitialized.current) return;
            hasInitialized.current = true;

            try {
                const { data: { session }, error } = await supabase.auth.getSession();
                if (error) {
                    console.error('[Auth] getSession error:', error);
                    throw error;
                }
                if (session) {
                    await hydrateUserFromSession(session);
                } else {
                    setLoading(false);
                    setHydrationComplete(true);
                    hydrationCompleteRef.current = true;
                }
            } catch (error) {
                console.error('Error during session initialization:', error);
                await clearSessionAndSignOut();
                setHydrationComplete(true);
                hydrationCompleteRef.current = true;
            }
        };

        initialize();

        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event: string, session: any) => {
            if (!session) {
                resetUserState(event === 'SIGNED_OUT');
                setLoading(false);
                setHydrationComplete(true);
                hydrationCompleteRef.current = true;
                return;
            }

            if (event === 'SIGNED_OUT') {
                resetUserState(true);
                setLoading(false);
                return;
            }

            if (event === 'INITIAL_SESSION' && hydrationCompleteRef.current) {
                return; // already hydrated; skip redundant runs
            }

            // Skip if hydration is already running for the same user
            if (hydrationInProgress.current && session.user?.id === lastHydratedUserId.current) {
                return;
            }

            if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED' || event === 'INITIAL_SESSION') {
                // Background update only if user is different or we are not yet hydrated
                if (session.user?.id !== lastHydratedUserId.current || !hydrationCompleteRef.current) {
                    await hydrateUserFromSession(session);
                }
            }
        });

        return () => subscription.unsubscribe();
    }, [hydrateUserFromSession, clearSessionAndSignOut, resetUserState]);

    // Loading timeout to prevent indefinite spinner on failure
    useEffect(() => {
        if (loading) {
            const timer = setTimeout(() => {
                console.warn('[Auth] Loading timeout - forcing resolution');
                setLoadingTimeout(true);
                setLoading(false);
                setHydrationComplete(true);
            }, 8000); // 8 seconds
            return () => clearTimeout(timer);
        } else {
            setLoadingTimeout(false);
        }
    }, [loading]);

    // While hydrating session, avoid rendering routes that could redirect to home/login
    if ((loading && !loadingTimeout) || !hydrationComplete) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                fontSize: '18px',
                color: '#666',
                background: 'var(--bg)'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ marginBottom: '1rem' }}>Loading...</div>
                    <div style={{ fontSize: '14px', color: 'var(--muted)' }}>Please wait while we verify your session</div>
                </div>
            </div>
        );
    }

    // Helper function to get default route based on role
    const getDefaultRoute = (role: string | null) => {
        if (!role || !userProfile) return '/login';

        switch (role) {
            case 'admin':
                return '/admin/dashboard';
            case 'client':
                const companyId = userProfile.company_id;
                if (!companyId) return '/company/first-run';
                return `/client/company/dashboard`;
            case 'ngo':
                const ngoId = userProfile.ngo_id;
                return ngoId ? `/ngo/ngo/dashboard` : '/ngo/accept-invite';
            default:
                return '/login';
        }
    };

    // If not authenticated, show public pages (no header/navigation)
    if (!user) {
        return (
            <div className="app" style={{ minHeight: '100vh' }}>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/company/subscription" element={<Subscription />} />
                    <Route path="/ngo/signup" element={<NGOSignup />} />
                    <Route path="/ngo/accept-invite" element={<AcceptInvite />} />
                    <Route path="/" element={<Home />} />
                    <Route path="/contact-us" element={<ContactUs />} />
                    <Route path="/calculator" element={<CSRCalculator />} />
                    <Route path="/why" element={<Why />} />
                    <Route path="/what" element={<What />} />
                    <Route path="/how" element={<How />} />
                    {/* Redirect any other unknown path to Home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </div>
        );
    }

    // Determine if we are currently inside the NGO onboarding process
    const isNgoSignupPath = window.location.pathname.includes('/ngo/signup') || 
                           window.location.pathname.includes('/ngo/accept-invite') ||
                           window.location.pathname.includes('/ngo/first-run');

    // SECURITY ENHANCEMENT: Tenant Route Guard
    if (userRole === 'client' && userProfile?.company_id && location.pathname.startsWith('/client/')) {
        if (!location.pathname.startsWith(`/client/company/`)) {
            return <Navigate to={`/client/company/dashboard`} replace />;
        }
        
        if (userProfile?.role?.toLowerCase().includes('viewer') && !location.pathname.endsWith('/dashboard') && !location.pathname.endsWith('/journey-so-far') && !location.pathname.endsWith('/mom-tracker')) {
            return <Navigate to={`/client/company/dashboard`} replace />;
        }
    }

    if (userRole === 'ngo' && userProfile?.ngo_id && location.pathname.startsWith('/ngo/')) {
        if (!location.pathname.startsWith(`/ngo/ngo/`) && !isNgoSignupPath) {
            return <Navigate to={`/ngo/ngo/dashboard`} replace />;
        }
    }

    // If authenticated but role is invalid, only redirect if hydration is complete and NOT currently running in background
    if (user && !userRole && !loading && hydrationComplete && !hydrationInProgress.current) {
        if (!isNgoSignupPath && location.pathname !== '/login') {
            if (userProfile && userProfile.role) {
                console.warn("User authenticated with profile but role normalization failed. Role:", userProfile.role);
            } else {
                console.warn("User authenticated but role is invalid after hydration. Redirecting to login.");
                return <Navigate to="/login" replace />;
            }
        }
    }

    if (userRole === 'ngo' && !loading) {
        if (!userProfile?.ngo_id && !isNgoSignupPath) {
            return <Navigate to="/ngo/signup" replace />;
        }
    }

    if (userRole === 'client' && (!userProfile?.company_id || !userProfile?.profile_complete)) {
        // If we are still hydrating the profile in the background, only show loading if we don't have a profile yet.
        // If we already have a profile, we continue rendering the current route while the background fetch updates the state.
        if (hydrationInProgress.current && !userProfile) {
            return (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    fontSize: '18px',
                    color: '#666',
                    background: 'var(--bg)'
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ marginBottom: '1rem' }}>Loading...</div>
                        <div style={{ fontSize: '14px', color: 'var(--muted)' }}>Fetching profile details...</div>
                    </div>
                </div>
            );
        }

        const isNgoSignupPath = window.location.pathname.includes('/ngo/signup') || window.location.pathname.includes('/ngo/accept-invite');
        const isCompanyInvitePath = window.location.pathname.includes('/company/accept-invite');

        if (userProfile?.role?.toLowerCase().includes('viewer') && !userProfile?.company_id) {
            return (
                <div className="app">
                    <Routes>
                        <Route path="/waiting-assignment" element={<WaitingAssignment />} />
                        <Route path="*" element={<Navigate to="/waiting-assignment" replace />} />
                    </Routes>
                </div>
            );
        }

        if (!isNgoSignupPath) {
            return (
                <div className="app">
                    <Routes>
                        <Route path="/company/accept-invite" element={<CompanyAcceptInvite />} />
                        <Route path="/company/first-run" element={<CompanyFirstRun />} />
                        <Route path="*" element={<Navigate to={isCompanyInvitePath ? "/company/accept-invite" : "/company/first-run"} replace />} />
                    </Routes>
                </div>
            );
        }
    }

    // For authenticated users, show layout with sidebar
    const isNGOPublicPage = location.pathname === '/ngo/signup' || location.pathname === '/ngo/accept-invite';

    return (
        <AuthProvider userRole={userRole} userProfile={userProfile}>
        <div className="app">
            {userProfile?.original_role === 'admin' && userRole !== 'admin' && (
                <button 
                    onClick={() => { localStorage.removeItem('admin_impersonated_company_id'); window.location.href = '/admin/dashboard'; }} 
                    style={{ position: 'fixed', bottom: '20px', left: '20px', zIndex: 9999, padding: '10px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '50px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.3)', fontSize: '0.9rem' }}
                >
                    🏠 Back to Admin
                </button>
            )}
            {/* Header - Hide on NGO public pages */}
            {!isNGOPublicPage && (
                <header className="app-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000 }}>
                    <a
                        href="https://www.tblservices.in/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="logo"
                        style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', flex: 1 }}
                    >
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <img src={logoImage} alt="TBL Impact Anchor" style={{ height: '45px', objectFit: 'contain' }} />
                            <span style={{
                                position: 'absolute',
                                top: '-2px',
                                right: '2px',
                                fontSize: '0.45rem',
                                color: '#000',
                                fontWeight: '900',
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                                pointerEvents: 'none'
                            }}>BETA</span>
                        </div>
                    </a>


                    {userRole === 'client' && userProfile?.company_id && (
                        <nav className={`header-nav ${isMobileMenuOpen ? 'mobile-open' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>
                            <Link to={`/client/company/dashboard`} className={location.pathname.includes('/dashboard') ? 'active' : ''}>
                                Dashboard
                            </Link>
                            {userProfile?.setup_type === 'single' && (
                                <Link to={`/client/company/journey-so-far`} className={location.pathname.includes('/journey-so-far') ? 'active' : ''}>
                                    Journey So Far
                                </Link>
                            )}
                            {(() => {
                                const isViewer = userProfile?.role?.toLowerCase().includes('viewer');
                                
                                if (isViewer) {
                                    return (
                                        <>
                                        {/* <Link to={`/client/company/mom-tracker`} className={location.pathname.includes('/mom-tracker') ? 'active' : ''}>
                                            MOM Tracker
                                        </Link> */}
                                        <div className="mobile-only mobile-actions-container">
                                            <Link to={`/client/company/settings`} className="mobile-action-btn" onClick={() => setIsMobileMenuOpen(false)}>
                                                <span style={{ marginRight: '0.5rem' }}>⚙️</span> Settings
                                            </Link>
                                            <button className="mobile-action-btn logout-btn" onClick={() => { clearSessionAndSignOut(); setIsMobileMenuOpen(false); }}>
                                                <span style={{ marginRight: '0.5rem' }}>🚪</span> Sign Out
                                            </button>
                                        </div>
                                    </>
                                );
                                }
                                
                                const handleViewerClick = (e: React.MouseEvent) => {};
                                const viewerStyle = {};
                                const tooltipText = undefined;

                                return (
                                    <>
                                        <CustomTooltipLink tooltipText={tooltipText} onClick={handleViewerClick} to={`/client/company/corporate-financials`} className={location.pathname.includes('/corporate-financials') ? 'active' : ''} style={viewerStyle}>
                                            Company
                                        </CustomTooltipLink>
                                        <CustomTooltipLink tooltipText={tooltipText} onClick={handleViewerClick} to={`/client/company/projects`} className={location.pathname.includes('/projects') && !location.pathname.includes('/projects/create') ? 'active' : ''} style={{ position: 'relative', ...viewerStyle }}>
                                            Programs
                                            {(badges.projects || badges.ngos) && !isViewer && <BadgeDot />}
                                        </CustomTooltipLink>
                                        <CustomTooltipLink tooltipText={tooltipText} onClick={handleViewerClick} to={`/client/company/compliance`} className={location.pathname.includes('/compliance') ? 'active' : ''} style={{ position: 'relative', ...viewerStyle }}>
                                            Compliance
                                            {badges.compliance && !isViewer && <BadgeDot />}
                                        </CustomTooltipLink>
                                        <CustomTooltipLink tooltipText={tooltipText} onClick={handleViewerClick} to={`/client/company/finance`} className={location.pathname.includes('/finance') ? 'active' : ''} style={{ position: 'relative', ...viewerStyle }}>
                                            Finance
                                            {badges.finance && !isViewer && <BadgeDot />}
                                        </CustomTooltipLink>
                                        <CustomTooltipLink tooltipText={tooltipText} onClick={handleViewerClick} to={`/client/company/impact`} className={location.pathname.includes('/impact') ? 'active' : ''} style={{ position: 'relative', ...viewerStyle }}>
                                            Impact
                                            {badges.impact && !isViewer && <BadgeDot />}
                                        </CustomTooltipLink>
                                        {/* <CustomTooltipLink tooltipText={tooltipText} onClick={handleViewerClick} to={`/client/company/mom-tracker`} className={location.pathname.includes('/mom-tracker') ? 'active' : ''} style={{ position: 'relative', ...viewerStyle }}>
                                            MOM Tracker
                                        </CustomTooltipLink> */}
                                        <div className="mobile-only mobile-actions-container">
                                            <Link to={`/client/company/settings`} className="mobile-action-btn" onClick={() => setIsMobileMenuOpen(false)}>
                                                <span style={{ marginRight: '0.5rem' }}>⚙️</span> Settings
                                            </Link>
                                            <button className="mobile-action-btn logout-btn" onClick={() => { clearSessionAndSignOut(); setIsMobileMenuOpen(false); }}>
                                                <span style={{ marginRight: '0.5rem' }}>🚪</span> Sign Out
                                            </button>
                                        </div>
                                    </>
                                );
                            })()}
                        </nav>
                    )}
                    {userRole === 'ngo' && userProfile?.ngo_id && (
                        <nav className={`header-nav ${isMobileMenuOpen ? 'mobile-open' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>
                            <CustomTooltipLink to={`/ngo/ngo/dashboard`} className={location.pathname.includes('/dashboard') ? 'active' : ''}>
                                Dashboard
                            </CustomTooltipLink>
                            <CustomTooltipLink to={`/ngo/ngo/projects`} className={location.pathname.includes('/projects') && !location.pathname.includes('/projects/') ? 'active' : ''} style={{ position: 'relative' }}>
                                Projects
                                {badges.projects && <BadgeDot />}
                            </CustomTooltipLink>
                            <CustomTooltipLink to={`/ngo/ngo/compliance`} className={location.pathname.includes('/compliance') ? 'active' : ''} style={{ position: 'relative' }}>
                                Compliance
                                {badges.compliance && <BadgeDot />}
                            </CustomTooltipLink>
                            <CustomTooltipLink to={`/ngo/ngo/finance`} className={location.pathname.includes('/finance') || location.pathname.includes('/budget') ? 'active' : ''} style={{ position: 'relative' }}>
                                Finance
                                {badges.finance && <BadgeDot />}
                            </CustomTooltipLink>
                            <CustomTooltipLink to={`/ngo/ngo/impact`} className={location.pathname.includes('/impact') ? 'active' : ''} style={{ position: 'relative' }}>
                                Impact
                                {badges.impact && <BadgeDot />}
                            </CustomTooltipLink>
                            <div className="mobile-only mobile-actions-container">
                                <Link to={`/ngo/ngo/settings`} className="mobile-action-btn" onClick={() => setIsMobileMenuOpen(false)}>
                                    <span style={{ marginRight: '0.5rem' }}>⚙️</span> Settings
                                </Link>
                                <button className="mobile-action-btn logout-btn" onClick={() => { clearSessionAndSignOut(); setIsMobileMenuOpen(false); }}>
                                    <span style={{ marginRight: '0.5rem' }}>🚪</span> Sign Out
                                </button>
                            </div>
                        </nav>
                    )}
                    {userRole === 'admin' && (
                        <nav className={`header-nav ${isMobileMenuOpen ? 'mobile-open' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>
                            <Link to="/admin/dashboard" className={location.pathname.includes('/admin') ? 'active' : ''}>
                                Admin Dashboard
                            </Link>
                            <div className="mobile-only mobile-actions-container">
                                <button className="mobile-action-btn logout-btn" onClick={() => { clearSessionAndSignOut(); setIsMobileMenuOpen(false); }}>
                                    <span style={{ marginRight: '0.5rem' }}>🚪</span> Sign Out
                                </button>
                            </div>
                        </nav>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, justifyContent: 'flex-end' }}>
                        {(userRole === 'client' && userProfile?.company_id) || (userRole === 'ngo' && userProfile?.ngo_id) ? (
                            <NotificationBell
                                userRole={userRole as 'client' | 'ngo'}
                                companyId={userProfile?.company_id}
                                ngoId={userProfile?.ngo_id}
                            />
                        ) : null}

                        <div className="profile-container desktop-only" ref={profileMenuRef}>
                            <button
                                className="profile-button"
                                onClick={() => setIsProfileMenuOpen((prev) => !prev)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '0.5rem',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#219653',
                                    transition: 'background-color 0.2s, color 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f5fdf7';
                                    e.currentTarget.style.color = '#17643a';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#219653';
                                }}
                                aria-haspopup="menu"
                                aria-expanded={isProfileMenuOpen}
                                title="Open profile menu"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                </svg>
                            </button>
                            {isProfileMenuOpen && (
                                <div className="profile-dropdown" role="menu">
                                    <div className="user-info">
                                        <div className="role">{userProfile?.role || userRole || 'User'}</div>
                                        <div className="greeting">{user?.email ?? 'Unknown user'}</div>
                                        {userProfile?.company_id && (
                                            <div className="email">Company ID: {userProfile.company_id}</div>
                                        )}
                                        {userProfile?.ngo_id && (
                                            <div className="email">NGO ID: {userProfile.ngo_id}</div>
                                        )}
                                    </div>
                                    {userRole === 'client' && userProfile?.company_id && (
                                        <Link
                                            to={`/client/company/settings`}
                                            className="settings-button"
                                            onClick={() => setIsProfileMenuOpen(false)}
                                            role="menuitem"
                                        >
                                            ⚙️ Settings
                                        </Link>
                                    )}
                                    {userRole === 'ngo' && userProfile?.ngo_id && (
                                        <Link
                                            to={`/ngo/ngo/settings`}
                                            className="settings-button"
                                            onClick={() => setIsProfileMenuOpen(false)}
                                            role="menuitem"
                                        >
                                            ⚙️ Settings
                                        </Link>
                                    )}
                                    {userRole === 'admin' && (
                                        <Link
                                            to="/admin/dashboard"
                                            className="settings-button"
                                            onClick={() => setIsProfileMenuOpen(false)}
                                            role="menuitem"
                                        >
                                            🏠 Corporate Dashboard
                                        </Link>
                                    )}
                                    <button className="logout-button" onClick={clearSessionAndSignOut} role="menuitem">
                                        Sign Out
                                    </button>
                                </div>
                            )}
                        </div>

                        <button 
                            className="mobile-menu-btn" 
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            aria-label="Toggle mobile menu"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                {isMobileMenuOpen ? (
                                    <>
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </>
                                ) : (
                                    <>
                                        <line x1="3" y1="12" x2="21" y2="12"></line>
                                        <line x1="3" y1="6" x2="21" y2="6"></line>
                                        <line x1="3" y1="18" x2="21" y2="18"></line>
                                    </>
                                )}
                            </svg>
                        </button>
                    </div>
                </header>
            )}

            {/* Layout with Sidebar */}
            <div className="app-layout">
                {/* Sidebar - Hidden (navigation is in header) */}
                {/* Sidebar removed - all navigation is in the header */}

                {/* Main Content */}
                <main className={`main-content ${isNGOPublicPage ? 'no-header' : ''}`}>
                    <Routes>
                        {/* Client Routes */}
                        {/* Routes for users with company_id */}
                        {userRole === 'client' && userProfile?.company_id && (
                            <>
                                <Route path="/client/company/dashboard" element={<CompanyDashboard />} />
                                <Route path="/client/company/journey-so-far" element={<JourneySoFar />} />
                                <Route path="/client/company/corporate-financials" element={<CorporateFinancials />} />
                                <Route path="/client/company/settings" element={<CompanySettings />} />
                                <Route path="/client/company/projects" element={<CompanyProjects />} />
                                <Route path="/client/company/projects/create" element={<ProjectCreate />} />
                                <Route path="/client/company/projects/:projectId/review" element={<ProjectReview />} />
                                <Route path="/client/company/projects/:projectId/assign" element={<ProjectAssign />} />
                                <Route path="/client/company/projects/:projectId/checklist" element={<PreStartChecklist />} />
                                <Route path="/client/company/projects/:projectId/close" element={<ProjectClosure />} />
                                <Route path="/client/company/compliance" element={<CompanyCompliance />} />
                                <Route path="/client/company/finance" element={<CompanyFinance />} />
                                <Route path="/client/company/impact" element={<CompanyImpact />} />
                                <Route path="/client/company/mom-tracker" element={<CompanyMOMTracker />} />
                                <Route path="/client/company/fund-requests/:requestId/review" element={<FundRequestReview />} />
                                <Route path="/client/company/ngos/invite" element={<NGOInvite />} />
                                <Route path="/client/company/ngos/:ngoId/review" element={<NGOReview />} />
                            </>
                        )}

                        {/* NGO Onboarding Routes (Accessible even before ngo_id is fully hydrated in state) */}
                        <Route path="/ngo/accept-invite" element={<AcceptInvite />} />
                        <Route path="/ngo/ngo/first-run" element={<NGOFirstRun />} />
                        <Route path="/ngo/signup" element={<NGOSignup />} />

                        {/* Protected NGO Routes (Require ngo_id) */}
                        {userRole === 'ngo' && userProfile?.ngo_id && (
                            <>
                                <Route path="/ngo/ngo/settings" element={<NGOSettings />} />
                                <Route path="/ngo/ngo/dashboard" element={<NGODashboard />} />
                                <Route path="/ngo/ngo/projects" element={<NGOProjects />} />
                                <Route path="/ngo/ngo/finance" element={<NGOFinance user={user} />} />
                                <Route path="/ngo/ngo/budget" element={<NGOFinance user={user} />} />
                                <Route path="/ngo/ngo/impact" element={<NGOImpact user={user} />} />
                                <Route path="/ngo/ngo/projects/:projectId/accept" element={<ProjectAccept />} />
                                <Route path="/ngo/ngo/projects/:projectId/details" element={<ProjectDetails />} />
                                <Route path="/ngo/ngo/projects/:projectId/fund-request" element={<FundRequest />} />
                                <Route path="/ngo/ngo/projects/:projectId/kpi-report" element={<KPIReport />} />
                                {/* Lowercase compliance route (preferred) */}
                                <Route path="/ngo/ngo/compliance" element={<NGOComplianceTracker />} />
                                {/* Backward-compatible uppercase route */}
                                <Route path="/ngo/ngo/NGOComplianceTracker" element={<NGOComplianceTracker />} />
                            </>
                        )}

                        {/* Admin Routes */}
                        {userRole === 'admin' && (
                            <>
                                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                            </>
                        )}


                        {/* Public routes (still accessible if logged in) */}
                        {/* Redirect authenticated users from root to dashboard */}
                        <Route path="/" element={<Navigate to={getDefaultRoute(userRole)} replace />} />
                        <Route path="/contact-us" element={<ContactUs />} />
                        <Route path="/calculator" element={<CSRCalculator />} />
                        <Route path="/why" element={<Why />} />
                        <Route path="/what" element={<What />} />
                        <Route path="/how" element={<How />} />
                        <Route path="/ngo/accept-invite" element={<AcceptInvite />} />
                        <Route path="/ngo/signup" element={<NGOSignup />} />
                        <Route
                            path="/login"
                            element={
                                <Navigate
                                    to={userRole ? getDefaultRoute(userRole) : location.pathname}
                                    replace
                                />
                            }
                        />

                        {/* Redirect any unmatched route to the user's default dashboard */}
                        <Route path="*" element={<Navigate to={getDefaultRoute(userRole)} replace />} />
                    </Routes>
                    <Footer />
                </main>
            </div>
        </div>
        </AuthProvider>
    );
};

const App: React.FC = () => {
    return (
        <Router
            future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true
            }}
        >
            <AppContent />
        </Router>
    );
};

export default App;


